import type { RedeemableClassification } from "@/components/screens/Redeemables/Shared/Types/ListRedeemablesTypes";
import { EndpointService, type APIPaginatedResponse } from "@/types/api/api";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type { DiscountApplicableTo, DiscountType } from "./Discount";
import type {
  OrganisationAssociationType,
  OrganisationRelationship,
} from "@/types/api/contracts/organisations";
import type { StripeStatus } from "@/components/screens/ManageAccounts/LinkedAccounts/Types/LinkedAccountsTypes";
import type { ValidDateFilter } from "@/components/screens/Redeemables/Redeemable/List/Types/ListRedeemablesTypes";

export interface IRedeemableAttachedConfiguration {
  cap: number;
  message: string;
  updated_at: string;
  menu_item_restrictions: Array<{
    error_message: string;
    menu_items: Array<{
      id: number;
    }>;
    redemption_count: number;
    redemption_limit: number;
    title: string;
  }>;
  timezone: string;
  applies_to_service_charge: boolean;
  usage_type: number;
  fixed_amount: number;
  start_date: string;
  discounted_menu_only: boolean;
  start_time: string;
  applicable_weekdays: number[];
  id: number;
  applicable_to: number;
  cap_per_head: number;
  daily_redemptions_cap: number | null;
  end_time: string;
  created_at: string;
  discount_type: number;
  percentage: number;
  end_date: string;
}

export interface IListConfiguration {
  name: string;
  id: number;
  number_of_restaurants: number;
  number_of_packages: number;
  start_date: string;
  end_date: string;
  redeemable_classification: RedeemableClassification;
  updated_at: string;
  created_at: string;
}

export interface IConfiguration extends IListConfiguration {
  applies_to_service_charge: boolean;
  applicable_to: DiscountApplicableTo;
  percentage: number;
  applicable_weekdays: Array<number>;
  end_time: string;
  fixed_amount: number;
  discounted_menu_only: boolean;
  message: string;
  customer_message?: string | null;
  usage_type: number;
  start_time: string;
  cap: number;
  menu_item_restrictions: Array<{
    error_message: string;
    menu_items: Array<{
      id: number;
    }>;
    redemption_count: number;
    redemption_limit: number;
    title: string;
  }>;
  discount_type: DiscountType;
  cap_per_head: number;
  daily_redemptions_cap: number | null;
  timezone: string;
}

export interface IListConfigurations
  extends APIPaginatedResponse<IListConfiguration> {}

export interface IListConfigurationsParams {
  date_range?: ValidDateFilter;
  search?: string;
  redeemable_classification?: Array<RedeemableClassification>;
  venue_id?: number;
  page?: number;
  page_size?: number;
}

/**
 * Interface representing the base parameters for creating a redeemable configuration.
 * @interface IBaseCreateConfigurationParams
 * @property {string} name — The name of the configuration.
 * @property {RedeemableClassification} redeemable_classification — The classification type of the redeemable.
 * @property {string} message — The message displayed on redemption.
 * @property {string | null} customer_message — Optional customer-facing message; null when unset.
 * @property {string} start_date — The start date of the configuration.
 * @property {string} end_date — The end date of the configuration.
 * @property {Array<number>} applicable_weekdays — The weekdays the configuration applies to.
 * @property {number | null} daily_redemptions_cap — The maximum number of daily redemptions, or null for no limit.
 */
export interface IBaseCreateConfigurationParams {
  name: string;
  redeemable_classification: RedeemableClassification;
  message: string;
  customer_message?: string | null;
  start_date: string;
  end_date: string;
  applicable_weekdays: Array<number>;
  daily_redemptions_cap?: number | null;
  applies_to_service_charge: boolean;
}

export interface IDiscountConfigurationParams
  extends IBaseCreateConfigurationParams {
  percentage?: number;
  fixed_amount?: number;
  applicable_to: DiscountApplicableTo;
  discount_type: DiscountType;
  cap_per_head?: number;
}

export interface IVoucherConfigurationParams
  extends IBaseCreateConfigurationParams {
  cap: number;
  percentage: number;
  recipient_email: string;
  recipient_name: string;
}

export interface ITabConfigurationParams
  extends IBaseCreateConfigurationParams {
  cap: number;
  recipient_email: string;
  recipient_name: string;
}

export interface IListConfigurationResponseAll {
  pages: Array<IListConfigurations>;
  pageParams: number[];
}

export interface IUpdateDiscountParams
  extends Omit<IDiscountConfigurationParams, "redeemable_classification"> {
  id: number;
  start_time: string;
  end_time: string;
  cap_per_head?: number;
}
export interface IUpdateVoucherParams
  extends Omit<IVoucherConfigurationParams, "redeemable_classification"> {
  id: number;
  start_time: string;
  end_time: string;
}
export interface IUpdateTabParams
  extends Omit<ITabConfigurationParams, "redeemable_classification"> {
  id: number;
  start_time: string;
  end_time: string;
}

export interface IDeleteConfigurationParams {
  id: number;
}

/**
 * Interface representing the parameters for duplicating a redeemable configuration.
 * @interface IDuplicateConfigurationParams
 * @property {number} id — The ID of the configuration to duplicate.
 * @property {string} name — The name for the duplicated configuration.
 * @property {string} message — The message for the duplicated configuration.
 */
export interface IDuplicateConfigurationParams {
  id: number;
  name: string;
  message: string;
}

export interface IListRestaurantRelationshipsParams {
  id: number;
  start_date?: string;
  end_date?: string;
  association_type?: OrganisationAssociationType;
  search?: string;
}

export interface IListRestaurantRelationship {
  restaurant: {
    stripe_status: StripeStatus;
    stripe_payouts_enabled: boolean;
    name: string;
    id: number | string;
    external_id?: string;
  };
  organisation_relation: Omit<
    OrganisationRelationship,
    "restaurant" | "access_status"
  >;
  id: number | string;
}

export interface IRetrieveConfigurationParams {
  id: number;
}

export interface IListRestaurantRelationshipsResponse
  extends APIPaginatedResponse<IListRestaurantRelationship> {}

export interface ICreateRestaurantRelationshipParams {
  id: number | string;
  creation_restaurant_ids: Array<number>;
  deletion_restaurant_ids: Array<number>;
  creation_restaurant_ids_data?: Array<OrganisationRelationship>;
  deletion_restaurant_ids_data?: Array<
    IListRestaurantRelationship["restaurant"]
  >;
}

export interface ICreateRestaurantRelationshipResponse {
  created_count: number;
  deleted_count: number;
}

export interface IListConfigurationPackagesRelationshipsParams {
  package_id?: number;
  configuration_id?: number;
}

export interface IListConfigurationPackagesRelationship {
  id: number;
  package: {
    id: number;
    redeemable_configuration_count: number;
    menu_item_count: number;
    name: string;
    redemption_limit: number | string;
    created_at: string;
    updated_at: string;
  };
  configuration: {
    redeemable_classification: RedeemableClassification;
    start_date: string;
    updated_at: string;
    name: string;
    number_of_packages: number;
    end_date: string;
    created_at: string;
    id: number;
    number_of_restaurants: number;
  };
}

export interface IListConfigurationPackagesRelationshipsResponse
  extends APIPaginatedResponse<IListConfigurationPackagesRelationship> {}

export interface ICreateUpdatePackagesRelationshipParams {
  create: Array<{
    configuration_id: number;
    package_id: number;
  }>;
  // Relationship IDs to delete
  delete: Array<number>;
}

export interface ICreateUpdatePackagesRelationshipResponse {
  created_count: number;
  deleted_count: number;
}

export interface IConfigurationsService {
  list: (
    params?: IListConfigurationsParams,
  ) => UseQueryResult<IListConfigurations, Error>;
  listAll: (
    params?: IListConfigurationsParams,
  ) => UseInfiniteQueryResult<IListConfigurationResponseAll, Error>;
  retrieve: (
    params: IRetrieveConfigurationParams,
  ) => UseQueryResult<IConfiguration, Error>;
  create: (
    listParams?: IListConfigurationsParams,
  ) => UseMutationResult<
    IConfiguration,
    Error,
    | IDiscountConfigurationParams
    | IVoucherConfigurationParams
    | ITabConfigurationParams
  >;
  update: () => UseMutationResult<
    IConfiguration,
    Error,
    IUpdateDiscountParams | IUpdateVoucherParams | IUpdateTabParams
  >;
  delete: () => UseMutationResult<boolean, Error, IDeleteConfigurationParams>;
  duplicate: () => UseMutationResult<
    IConfiguration,
    Error,
    IDuplicateConfigurationParams
  >;

  // Restaurant linking
  listRestaurantRelationships: (
    params: IListRestaurantRelationshipsParams,
  ) => UseQueryResult<IListRestaurantRelationshipsResponse, Error>;
  listAllRestaurantRelationships: (
    params: IListRestaurantRelationshipsParams,
  ) => UseInfiniteQueryResult<IListRestaurantRelationshipsResponse, Error>;
  createUpdateRestaurantRelationship: (
    listParams?: IListRestaurantRelationshipsParams,
  ) => UseMutationResult<
    ICreateRestaurantRelationshipResponse,
    Error,
    ICreateRestaurantRelationshipParams
  >;

  // Configuration Linking
  listConfigurationPackagesRelationships: (
    params: IListConfigurationPackagesRelationshipsParams,
  ) => UseQueryResult<IListConfigurationPackagesRelationshipsResponse, Error>;
  createUpdatePackagesRelationship: (
    listParams?: Record<string, string | number | boolean>,
  ) => UseMutationResult<
    ICreateUpdatePackagesRelationshipResponse,
    Error,
    ICreateUpdatePackagesRelationshipParams
  >;
}

export class ConfigurationsService implements IConfigurationsService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params?: IListConfigurationsParams,
  ): UseQueryResult<IListConfigurations, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "configurations", "list"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IListConfigurations>>(
          "/promotions/configurations/",
          {
            params: updatedParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
    });
  }

  listAll(
    params?: IListConfigurationsParams,
  ): UseInfiniteQueryResult<IListConfigurationResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "configurations", "list-all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<AxiosResponse<IListConfigurations>>(
          "/promotions/configurations/",
          {
            params: { ...updatedParams, page: pageParam, page_size: 50 },
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      getNextPageParam: (lastPage: IListConfigurations) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  retrieve(
    params: IRetrieveConfigurationParams,
  ): UseQueryResult<IConfiguration, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "configurations", "retrieve", params.id],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IConfiguration>>(
          `/promotions/configurations/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
    });
  }

  create(
    listParams?: IListConfigurationsParams,
  ): UseMutationResult<
    IConfiguration,
    Error,
    | IDiscountConfigurationParams
    | IVoucherConfigurationParams
    | ITabConfigurationParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "configurations", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IConfiguration>>(
          "/promotions/configurations/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onMutate: async (newConfiguration) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "configurations", "list"],
            venueID: this.api.selectedVenue,
            params: listParams,
          }),
        });
        const previousConfigurations =
          queryClient.getQueryData<IListConfigurations>(
            buildQueryKey({
              queryKey: ["redeemables", "configurations", "list"],
              venueID: this.api.selectedVenue,
              params: listParams,
            }),
          );

        if (previousConfigurations) {
          queryClient.setQueryData<IListConfigurations>(
            buildQueryKey({
              queryKey: ["redeemables", "configurations", "list"],
              venueID: this.api.selectedVenue,
              params: listParams,
            }),
            (old) => {
              return {
                ...old,
                results: [
                  ...old.results,
                  {
                    id: -1, // Temporary ID for optimistic update
                    name: newConfiguration.name,
                    number_of_restaurants: 0,
                    number_of_packages: 0,
                    start_date: newConfiguration.start_date,
                    end_date: newConfiguration.end_date,
                    redeemable_classification:
                      newConfiguration.redeemable_classification,
                    updated_at: new Date().toISOString(),
                    created_at: new Date().toISOString(),
                  },
                ],
                count: old.count + 1,
                next: old.next,
                previous: old.previous,
              };
            },
          );
        }

        return { previousConfigurations };
      },
      onError: (err, previousConfigurations, context) => {
        if (context?.previousConfigurations) {
          queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "configurations", "list"],
              venueID: this.api.selectedVenue,
              params: listParams,
            }),
            context.previousConfigurations,
          );
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "configurations", "list"],
            venueID: this.api.selectedVenue,
            params: listParams,
          }),
        });
      },
    });
  }

  update(): UseMutationResult<
    IConfiguration,
    Error,
    IUpdateDiscountParams | IUpdateVoucherParams | IUpdateTabParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "configurations", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IConfiguration>>(
          `/promotions/configurations/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (params) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "configurations", "retrieve", params.id],
            venueID: this.api.selectedVenue,
            params: { id: params.id },
          }),
        });
        const previousConfiguration =
          await queryClient.getQueryData<IConfiguration>(
            buildQueryKey({
              queryKey: [
                "redeemables",
                "configurations",
                "retrieve",
                params.id,
              ],
              venueID: this.api.selectedVenue,
              params: { id: params.id },
            }),
          );
        if (previousConfiguration) {
          await queryClient.setQueryData<IConfiguration>(
            buildQueryKey({
              queryKey: [
                "redeemables",
                "configurations",
                "retrieve",
                params.id,
              ],
              venueID: this.api.selectedVenue,
              params: { id: params.id },
            }),
            (old) => {
              return {
                ...old,
                ...params,
              };
            },
          );
        }
        return { previousConfiguration };
      },
      onSuccess: async (data, params) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "configurations", "retrieve", params.id],
            venueID: this.api.selectedVenue,
            params: { id: params.id },
          }),
        });
      },
      onError: (err, params, context) => {
        if (context?.previousConfiguration) {
          queryClient.setQueryData(
            buildQueryKey({
              queryKey: [
                "redeemables",
                "configurations",
                "retrieve",
                params.id,
              ],
              venueID: this.api.selectedVenue,
              params: { id: params.id },
            }),
            context.previousConfiguration,
          );
        }
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteConfigurationParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "configurations", "delete"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id } = params;
        const { data } = await this.api.delete<AxiosResponse<string>>(
          `/promotions/configurations/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data === "";
      },
    });
  }

  duplicate(): UseMutationResult<
    IConfiguration,
    Error,
    IDuplicateConfigurationParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "configurations", "duplicate"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.post<AxiosResponse<IConfiguration>>(
          `/promotions/configurations/${id}/duplicate/`,
          {
            params: restParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "configurations", "list"],
            venueID: this.api.selectedVenue,
            params: {},
          }),
        });
      },
    });
  }

  // Restaurant linking
  listRestaurantRelationships(
    params: IListRestaurantRelationshipsParams,
  ): UseQueryResult<IListRestaurantRelationshipsResponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "redeemables",
          "configurations",
          "list-restaurant-relationships",
        ],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { id, ...restParams } = params;
        const { data } = await this.api.get<
          AxiosResponse<IListRestaurantRelationshipsResponse>
        >(`/promotions/configurations/${id}/restaurants/`, {
          params: restParams,
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return data;
      },
    });
  }

  listAllRestaurantRelationships(
    params: IListRestaurantRelationshipsParams,
  ): UseInfiniteQueryResult<IListRestaurantRelationshipsResponse, Error> {
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "redeemables",
          "configurations",
          "list-all-restaurant-relationships",
        ],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.get<
          AxiosResponse<IListRestaurantRelationshipsResponse>
        >(`/promotions/configurations/${id}/restaurants/`, {
          params: { ...restParams, page: pageParam, page_size: 100 },
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return data;
      },
      getNextPageParam: (lastPage: IListRestaurantRelationshipsResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  createUpdateRestaurantRelationship(
    listParams?: IListRestaurantRelationshipsParams,
  ): UseMutationResult<
    ICreateRestaurantRelationshipResponse,
    Error,
    ICreateRestaurantRelationshipParams
  > {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: [
          "redeemables",
          "configurations",
          "create-restaurant-relationship",
        ],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const {
          id,
          creation_restaurant_ids_data,
          deletion_restaurant_ids_data,
          ...restParams
        } = params;

        const { data } = await this.api.post<
          AxiosResponse<ICreateRestaurantRelationshipResponse>
        >(`/promotions/configurations/${id}/restaurants/bulk`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });
        return data;
      },

      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: [
              "redeemables",
              "configurations",
              "list-all-restaurant-relationships",
            ],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  // Configuration Linking
  listConfigurationPackagesRelationships(
    params: IListConfigurationPackagesRelationshipsParams,
  ): UseQueryResult<IListConfigurationPackagesRelationshipsResponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "redeemables",
          "configurations",
          "list-configuration-packages-relationships",
        ],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IListConfigurationPackagesRelationshipsResponse>
        >("/promotions/configuration_packages/", {
          params: params,
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return data;
      },
      enabled: !!params.configuration_id || !!params.package_id,
    });
  }
  createUpdatePackagesRelationship(
    listParams?: Record<string, string | number | boolean>,
  ): UseMutationResult<
    ICreateUpdatePackagesRelationshipResponse,
    Error,
    ICreateUpdatePackagesRelationshipParams
  > {
    const queryClient = useQueryClient();
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: [
          "redeemables",
          "configurations",
          "create-packages-relationship",
        ],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<
          AxiosResponse<ICreateUpdatePackagesRelationshipResponse>
        >("/promotions/configuration_packages/bulk/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: params,
        });
        return data;
      },
      onMutate: async (params) => {},
      onSuccess: async (data, params) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: [
              "redeemables",
              "configurations",
              "list-configuration-packages-relationships",
            ],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
      onError: async (err, params, context) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: [
              "redeemables",
              "configurations",
              "list-configuration-packages-relationships",
            ],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }
}
